<script setup>
/**
 * Under a member's "Bookings via RM" on the restaurant's Fans page (Kapil,
 * 2026-09-25): their highlight — the note icon with the words the restaurant
 * got, and when — or, when they keep a note about the restaurant they have
 * not shared, "the note icon filled in… even if private", with no words.
 *
 * Drawn the way a member's friends see the same thing on their Favorites
 * (MemberFavorites): the filled icon, and a tap that says the notes are kept
 * private. Rules in utils/restaurantHighlights (tested).
 */
import { computed, ref } from 'vue'
import { fanNoteLine, privateNoteHint } from '@/utils/restaurantHighlights'

const props = defineProps({
  fan: { type: Object, required: true },

  // The restaurant the list is for, for the private-note hint.
  restaurantName: { type: String, default: '' },

  // Set when the team runs several restaurants: which one got the highlight.
  highlightRestaurantName: { type: String, default: '' },
})

const line = computed(() => fanNoteLine(props.fan))
const hint = computed(() => privateNoteHint(props.fan, props.restaurantName))
const hintOpen = ref(false)
</script>

<template>
  <div
    v-if="line"
    class="fan-note-line"
  >
    <template v-if="line.kind === 'highlight'">
      <VIcon
        icon="mdi-note-text"
        size="14"
        color="primary-dark"
        class="fan-note-line__icon"
      />
      <div class="fan-note-line__body">
        <p class="fan-note-line__text">
          {{ line.text }}
        </p>
        <p
          v-if="line.ago || highlightRestaurantName"
          class="fan-note-line__time"
        >
          {{ [highlightRestaurantName, line.ago].filter(Boolean).join(' · ') }}
        </p>
      </div>
    </template>
    <button
      v-else
      type="button"
      class="fan-note-line__private"
      :aria-label="hint"
      @click.stop="hintOpen = true"
    >
      <VIcon
        icon="mdi-note-text"
        size="14"
        color="primary-dark"
      />
    </button>

    <VDialog
      v-model="hintOpen"
      max-width="340"
    >
      <VCard class="pa-4">
        <p class="text-body-2 mb-4">
          {{ hint }}
        </p>
        <div class="d-flex justify-end">
          <VBtn
            variant="text"
            size="small"
            @click="hintOpen = false"
          >
            Close
          </VBtn>
        </div>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.fan-note-line {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 4px;
}

.fan-note-line__icon {
  flex: 0 0 auto;
  margin-top: 2px;
}

.fan-note-line__body {
  min-width: 0;
}

/* Their words, in their line breaks. */
.fan-note-line__text {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-line;
  overflow-wrap: anywhere;
  color: var(--ds-text-body, rgb(var(--v-theme-on-surface)));
}

.fan-note-line__time {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--ds-text-muted, rgba(var(--v-theme-on-surface), 0.6));
}

.fan-note-line__private {
  display: inline-flex;
  padding: 2px;
  border: 0;
  background: none;
  cursor: pointer;
}
</style>
